import { useEffect, useRef, useState } from "react";
import { Volume2, VolumeX, Play, Pause } from "lucide-react";

/**
 * GalleryVideoPlayer
 *
 * Why this exists: a plain `<video src controls playsInline>` shows a black box
 * on iOS Safari + mobile Chrome when the asset has no `poster` and no
 * `preload="metadata"`. Desktop browsers fetch + render the first frame anyway,
 * which is why the bug only reproduces on mobile.
 *
 * Strategy (works everywhere):
 *   1. Muted autoplay + loop + playsInline → mobile + desktop both render
 *      the video immediately so the user always sees content (no black box).
 *   2. Tap-for-sound overlay button → user opts in to audio if they want it.
 *   3. `preload="metadata"` + `#t=0.1` fragment → forces the first-frame fetch
 *      so even when autoplay is blocked the poster-equivalent frame shows.
 */
export default function GalleryVideoPlayer({
  src,
  poster,
  caption,
  className = "",
  testid,
}) {
  const ref = useRef(null);
  const [muted, setMuted] = useState(true);
  const [playing, setPlaying] = useState(true);
  const [needsTapToPlay, setNeedsTapToPlay] = useState(false);

  // Force the first frame to render even if autoplay is blocked
  const srcWithSeek = src
    ? (src.includes("#") ? src : `${src}#t=0.1`)
    : src;

  useEffect(() => {
    const v = ref.current;
    if (!v) return;
    // Best-effort autoplay; if the browser blocks (rare for muted), surface a tap CTA
    const tryPlay = async () => {
      try {
        await v.play();
        setPlaying(true);
        setNeedsTapToPlay(false);
      } catch {
        setPlaying(false);
        setNeedsTapToPlay(true);
      }
    };
    tryPlay();
  }, [src]);

  const toggleSound = (e) => {
    e?.stopPropagation?.();
    const v = ref.current;
    if (!v) return;
    const next = !muted;
    v.muted = next;
    setMuted(next);
    // iOS sometimes pauses when toggling audio — make sure we're still playing
    if (v.paused) v.play().catch(() => {});
  };

  const togglePlay = async () => {
    const v = ref.current;
    if (!v) return;
    if (v.paused) {
      try {
        await v.play();
        setPlaying(true);
        setNeedsTapToPlay(false);
      } catch {
        /* ignored */
      }
    } else {
      v.pause();
      setPlaying(false);
    }
  };

  return (
    <div
      className={`relative aspect-video w-full rounded-lg overflow-hidden bg-black ${className}`}
      data-testid={testid}
    >
      <video
        ref={ref}
        src={srcWithSeek}
        poster={poster || undefined}
        autoPlay
        muted={muted}
        loop
        playsInline
        preload="metadata"
        // Hide native controls on mobile so the muted-autoplay UX feels native;
        // power users can long-press to open the native menu on most platforms.
        className="w-full h-full object-contain bg-black"
        onClick={togglePlay}
        onPlay={() => { setPlaying(true); setNeedsTapToPlay(false); }}
        onPause={() => setPlaying(false)}
      />

      {/* Tap-to-play overlay shown only when autoplay was blocked */}
      {needsTapToPlay && (
        <button
          type="button"
          onClick={togglePlay}
          className="absolute inset-0 flex items-center justify-center bg-black/40 hover:bg-black/30 transition-colors"
          aria-label="Play video"
        >
          <span className="w-14 h-14 rounded-full bg-white/95 flex items-center justify-center shadow-lg">
            <Play className="w-6 h-6 text-black ml-0.5" fill="black" />
          </span>
        </button>
      )}

      {/* Sound + play/pause controls in a corner — always available */}
      <div className="absolute bottom-2 right-2 flex items-center gap-2">
        <button
          type="button"
          onClick={togglePlay}
          aria-label={playing ? "Pause video" : "Play video"}
          className="w-9 h-9 rounded-full bg-black/60 hover:bg-black/80 flex items-center justify-center backdrop-blur-sm transition-colors"
        >
          {playing ? <Pause className="w-4 h-4 text-white" /> : <Play className="w-4 h-4 text-white ml-0.5" />}
        </button>
        <button
          type="button"
          onClick={toggleSound}
          aria-label={muted ? "Unmute video" : "Mute video"}
          className="w-9 h-9 rounded-full bg-black/60 hover:bg-black/80 flex items-center justify-center backdrop-blur-sm transition-colors"
        >
          {muted ? <VolumeX className="w-4 h-4 text-white" /> : <Volume2 className="w-4 h-4 text-white" />}
        </button>
      </div>

      {/* "Tap for sound" hint while still muted (auto-fades on tap) */}
      {muted && !needsTapToPlay && (
        <button
          type="button"
          onClick={toggleSound}
          className="absolute top-2 left-2 px-2 py-1 rounded-full bg-black/60 backdrop-blur-sm text-white text-[10px] font-semibold flex items-center gap-1 hover:bg-black/80 transition-colors"
          aria-label="Tap for sound"
        >
          <VolumeX className="w-3 h-3" /> Tap for sound
        </button>
      )}

      {caption && (
        <div className="absolute bottom-0 left-0 right-0 p-2 bg-gradient-to-t from-black/70 to-transparent pointer-events-none">
          <p className="text-xs text-white truncate">{caption}</p>
        </div>
      )}
    </div>
  );
}
